<script setup lang="ts">
/**
 * Narrative detail / editor page — the open narrative with its Pages sidebar
 * (see docs_for_claude/figma-designs/latest/narrative-*.png). Reuses the same
 * shell as NarrativesPage (far-left icon rail + collapsible/resizable sub-nav +
 * main content), but in place of the section tab bar it shows a breadcrumb
 * ("Narratives / <name>") whose name is single-click-to-rename. The sub-nav is
 * NarrativePagesSidebar (the page tree). The main pane renders the selected
 * page's title as an editable H1 with the NarrativeEditor block editor below.
 *
 * Backed by the stub narratives API via narrativesStore (no real persistence
 * yet). Page content auto-saves on page switch / unmount.
 */
import { computed, nextTick, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import type { OutputData } from '@editorjs/editorjs';
import MatterNavRail from '../matter/MatterNavRail.vue';
import NarrativePagesSidebar from './NarrativePagesSidebar.vue';
import NarrativeEditor from './NarrativeEditor.vue';
import NarrativeFindingsPanel from './NarrativeFindingsPanel.vue';
import { useMatterStore } from '../../stores/matterStore';
import { useNarrativesStore } from '../../stores/narrativesStore';
import { useFindingsStore, type Finding as StoredFinding } from '../../stores/findingsStore';
import type { Finding as LinkableFinding } from './findingLinkTool';
import { useResizableSidebar } from '../../composables/useResizableSidebar';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const matterStore = useMatterStore();
const store = useNarrativesStore();
const findingsStore = useFindingsStore();

const narrativeId = computed(() => String(route.params.narrativeId));

// ── Findings side panel ────────────────────────────────────────────────────
// Opened by the top-right toggle in the workspace. Findings are loaded lazily
// the first time it opens (and on matter change while it's open).
const findingsPanelOpen = ref(false);
// When a finding link in the editor is clicked, the panel opens and this id is
// passed down so the matching row is revealed and briefly highlighted.
const highlightFindingId = ref<string | null>(null);

const toggleFindingsPanel = () => {
    findingsPanelOpen.value = !findingsPanelOpen.value;
    if (findingsPanelOpen.value) void loadFindings();
    else highlightFindingId.value = null;
};

// A finding link in the editor content was clicked — reveal it in the panel.
const onFindingClick = async (findingId: string) => {
    findingsPanelOpen.value = true;
    await loadFindings();
    highlightFindingId.value = findingId;
};

const loadFindings = async () => {
    const matterId = matterStore.currentMatter?.RecordId;
    if (!matterId || findingsStore.loadedMatterId === matterId) return;
    try {
        await findingsStore.fetchFindings(matterId);
    } catch {
        // findingsStore.error is surfaced in the panel.
    }
};

// Findings adapted to the editor's link-tool shape ({ id, code, label }). The
// findings API has no short "code", so we derive a stable one from the id
// prefix (index-based codes would shift as findings are added); the label is
// the finding's description. The durable key on the resulting anchor is the
// finding id, so this display sugar is safe to change.
const linkableFindings = computed<LinkableFinding[]>(() =>
    findingsStore.findings.map((f: StoredFinding) => ({
        id: f.id,
        code: `F-${f.id.replace(/-/g, '').slice(0, 6).toUpperCase()}`,
        label: f.description,
    })),
);

// Shared sub-nav collapse/resize (same persisted width as the Dialogues +
// Narratives sidebars). No side panels here, so it only collapses manually.
const {
    width: sidebarWidth,
    collapsed: navCollapsed,
    startResize: startSidebarResize,
    toggleCollapsed: toggleNav,
} = useResizableSidebar();

// ── Load the narrative ────────────────────────────────────────────────────
const activePageId = ref<string | null>(null);

watch(
    narrativeId,
    async (id) => {
        if (!id) return;
        try {
            await store.fetchNarrative(id);
            // Land on the first page.
            activePageId.value = store.current?.pages[0]?.id ?? null;
            // Load findings up front so the editor's "Link to finding" picker
            // is populated on first use (independent of the side panel toggle).
            void loadFindings();
        } catch {
            // currentError is surfaced below.
        }
    },
    { immediate: true },
);

const activePage = computed(
    () => store.current?.pages.find((p) => p.id === activePageId.value) ?? null,
);

// ── Editor wiring ─────────────────────────────────────────────────────────
const editorRef = useTemplateRef<InstanceType<typeof NarrativeEditor>>('editorRef');
// Bumping the key forces a fresh NarrativeEditor per page so its seed data
// (initialData) is re-read — Editor.js doesn't reactively swap `data`.
const editorKey = ref(0);

const emptyDoc = (): OutputData => ({ blocks: [], version: '2.31.6', time: 0 });

const editorSeed = ref<OutputData>(emptyDoc());

// Persist the current page's editor content back to the store. Called before
// switching pages and on unmount so edits aren't lost.
const persistActivePage = async (pageId: string | null) => {
    if (!pageId) return;
    const data = await editorRef.value?.save();
    if (!data) return;
    try {
        await store.savePageContent(narrativeId.value, pageId, data);
    } catch {
        // Best-effort; store surfaces the error.
    }
};

// When the active page changes, flush the outgoing page then reseed the editor
// for the incoming one.
watch(activePageId, async (newId, oldId) => {
    if (oldId && oldId !== newId) await persistActivePage(oldId);
    editorSeed.value = activePage.value?.content ?? emptyDoc();
    editorKey.value += 1;
});

const selectPage = async (pageId: string) => {
    if (pageId === activePageId.value) return;
    activePageId.value = pageId;
};

// If the active page was deleted, fall back to the first remaining page.
const onPageDeleted = (pageId: string) => {
    if (pageId !== activePageId.value) return;
    activePageId.value = store.current?.pages[0]?.id ?? null;
};

// ── Page title (editable H1) ──────────────────────────────────────────────
const pageTitleDraft = ref('');
watch(activePage, (page) => {
    pageTitleDraft.value = page?.title ?? '';
});

const commitPageTitle = async () => {
    const page = activePage.value;
    if (!page) return;
    const title = pageTitleDraft.value.trim();
    if (!title || title === page.title) {
        pageTitleDraft.value = page.title;
        return;
    }
    try {
        await store.renamePage(narrativeId.value, page.id, title);
    } catch {
        pageTitleDraft.value = page.title;
    }
};

// ── Narrative name (breadcrumb, single-click to rename) ───────────────────
const editingName = ref(false);
const nameDraft = ref('');
const nameInput = ref<HTMLInputElement | null>(null);
let nameCommitted = false;

const startNameEdit = async () => {
    if (!store.current) return;
    editingName.value = true;
    nameDraft.value = store.current.title;
    nameCommitted = false;
    await nextTick();
    nameInput.value?.focus();
    nameInput.value?.select();
};

const commitName = async () => {
    if (nameCommitted) return;
    nameCommitted = true;
    editingName.value = false;
    const title = nameDraft.value.trim();
    if (!store.current || !title || title === store.current.title) return;
    try {
        await store.renameNarrative(narrativeId.value, title);
    } catch {
        // Store surfaces the error.
    }
};

const cancelNameEdit = () => {
    nameCommitted = true;
    editingName.value = false;
};

const goToNarratives = () => {
    const matterId = matterStore.currentMatter?.RecordId;
    if (matterId) router.push({ name: 'narratives', params: { matterId } });
};

onBeforeUnmount(() => {
    void persistActivePage(activePageId.value);
});
</script>

<template>
    <div class="matter-page">
        <MatterNavRail />

        <div class="narrative-workspace">
            <!-- Breadcrumb bar (replaces the section tabs on the detail page). -->
            <nav class="narrative-breadcrumb" :aria-label="t('narratives.breadcrumb.aria')">
                <button type="button" class="narrative-crumb-link" @click="goToNarratives">
                    {{ t('narratives.tabs.narratives') }}
                </button>
                <span class="narrative-crumb-sep">/</span>
                <input
                    v-if="editingName"
                    ref="nameInput"
                    v-model="nameDraft"
                    type="text"
                    class="narrative-crumb-input"
                    :aria-label="t('narratives.breadcrumb.renameAria')"
                    @keyup.enter="commitName"
                    @keyup.esc="cancelNameEdit"
                    @blur="commitName"
                />
                <button
                    v-else
                    type="button"
                    class="narrative-crumb-current"
                    :title="t('narratives.breadcrumb.renameHint')"
                    @click="startNameEdit"
                >
                    {{ store.current?.title ?? t('narratives.editor.untitled') }}
                </button>
            </nav>

            <div class="narrative-workspace-body">
                <!-- Findings panel toggle, pinned to the workspace's top-right. -->
                <button
                    type="button"
                    class="narrative-findings-toggle"
                    :class="{ 'narrative-findings-toggle-active': findingsPanelOpen }"
                    :title="t('narratives.findingsPanel.toggle')"
                    :aria-label="t('narratives.findingsPanel.toggle')"
                    :aria-pressed="findingsPanelOpen"
                    @click="toggleFindingsPanel"
                >
                    <span class="material-icons-outlined">library_books</span>
                </button>

                <!-- Collapsed rail -->
                <transition name="mc-strip">
                    <nav
                        v-if="navCollapsed"
                        class="dialogues-nav-collapsed"
                        :aria-label="t('narratives.pages.collapsedAria')"
                    >
                        <button
                            type="button"
                            class="dialogues-collapsed-item"
                            :title="t('narratives.expand')"
                            :aria-label="t('narratives.expand')"
                            @click="toggleNav"
                        >
                            <span class="material-icons">chevron_right</span>
                        </button>
                    </nav>
                </transition>

                <!-- Pages sidebar -->
                <transition name="mc-sidebar">
                    <div
                        v-if="!navCollapsed"
                        class="sidebar"
                        :style="{ width: sidebarWidth + 'px' }"
                    >
                        <div class="matter-sidebar-header">
                            <button
                                type="button"
                                class="sidebar-collapse-btn"
                                :title="t('matters.sidebar.collapse')"
                                :aria-label="t('matters.sidebar.collapse')"
                                @click="toggleNav"
                            >
                                <svg class="nav-toggle-icon" viewBox="0 0 24 24" aria-hidden="true">
                                    <line x1="19" y1="5" x2="19" y2="19" />
                                    <line x1="13" y1="12" x2="4" y2="12" />
                                    <polyline points="8 8 4 12 8 16" />
                                </svg>
                            </button>
                        </div>
                        <NarrativePagesSidebar
                            v-if="store.current"
                            :narrative-id="narrativeId"
                            :active-page-id="activePageId"
                            @select="selectPage"
                            @page-deleted="onPageDeleted"
                        />
                        <div
                            class="sidebar-resize-handle"
                            :title="t('matters.sidebar.resize')"
                            @mousedown="startSidebarResize"
                        ></div>
                    </div>
                </transition>

                <!-- Page body -->
                <div class="main-content">
                    <div class="chat-section">
                        <div v-if="store.loadingCurrent && !store.current" class="findings-status">
                            <div class="loading-dots"><span></span><span></span><span></span></div>
                            <span>{{ t('narratives.detail.loading') }}</span>
                        </div>

                        <div
                            v-else-if="store.currentError && !store.current"
                            class="findings-status findings-status-error"
                        >
                            <span class="material-icons-outlined">error_outline</span>
                            <span>{{ t('narratives.detail.error') }}</span>
                            <button
                                type="button"
                                class="findings-retry-btn"
                                @click="store.fetchNarrative(narrativeId)"
                            >
                                {{ t('narratives.findings.retry') }}
                            </button>
                        </div>

                        <div v-else-if="activePage" class="narrative-page-body">
                            <input
                                v-model="pageTitleDraft"
                                type="text"
                                class="narrative-page-title"
                                :placeholder="t('narratives.editor.untitled')"
                                :aria-label="t('narratives.detail.pageTitleAria')"
                                @keyup.enter="($event.target as HTMLInputElement).blur()"
                                @blur="commitPageTitle"
                            />
                            <hr class="narrative-page-divider" />
                            <div class="narrative-page-editor">
                                <NarrativeEditor
                                    :key="editorKey"
                                    ref="editorRef"
                                    :initial-data="editorSeed"
                                    :findings="linkableFindings"
                                    :placeholder="t('narratives.detail.editorPlaceholder')"
                                    :autofocus="false"
                                    @finding-click="onFindingClick"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Findings side panel -->
                <transition name="mc-sidebar">
                    <NarrativeFindingsPanel
                        v-if="findingsPanelOpen"
                        :highlight-finding-id="highlightFindingId"
                        @close="findingsPanelOpen = false"
                    />
                </transition>
            </div>
        </div>
    </div>
</template>
